/* Copyright Flexday Solutions LLC, Inc - All Rights Reserved

 * Unauthorized copying of this file, via any medium is strictly prohibited

 * Proprietary and confidential

 * See file LICENSE.txt for full license details.

 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { object, string, array } from 'yup';
import { Form } from 'formik';

import PageContainer from '../../../components/pageContainer';
import { SECTIONS } from '../../../constants/drawer';
import AddPageHeader from '../../../components/form/addPageHeader.component';
import { ACCESS_CONTROL } from '../../../constants/path';
import { USERS_GROUP } from '../../../constants/accessControlPaths';
import AppForm, {
  AppAutocompleteField,
  AppSelectField,
  FormButtons,
} from '../../../components/form';
import { getChangedValues } from '../../../components/form/functions';
import { FormContainer, InputsContainer } from '../styled';
import {
  RBAC_USERS_GROUP_ENDPOINT,
  RBAC_GROUPS_ENDPOINT,
  RBAC_USERS_ENDPOINT,
} from '../../../constants/accessControl';
import { AlertStyled } from '../../settings/filesCollectionSettingsPage/styled';
import {
  useCreateRBACMutation,
  useLazyGetAllRBACQuery,
  useLazyGetOneRBACQuery,
  useUpdateRBACMutation,
} from '../../../redux/services/speciphicAsk';
import Loader from '../../../components/loader/loader.component';
import { Chip } from '@mui/material';
import { filterByIds } from '../../../utils/functions';

const mainPageRoute = `${ACCESS_CONTROL}${USERS_GROUP.endpoint}`;

const translationJSONPrefix = 'accessControlSection.usersGroup';

const defaultDataState = {
  group: '',
  users: [],
};

const formSchema = (t) => {
  const translationPrefix = (label) =>
    `${translationJSONPrefix}.form.fields.${label}.errorMessages`;

  return object({
    group: string().required(t(`${translationPrefix('group')}.required`)),
    users: array()
      .required(t(`${translationPrefix('users')}.required`))
      .min(2, t(`${translationPrefix('users')}.min`)),
  });
};

const formRenderFunction = (
  { values, errors, touched, dirty, isSubmitting, resetForm, setFieldValue },
  formSending,
  groupsData,
  isGroupsLoading,
  usersData,
  isUsersLoading,
) => {
  const { t } = useTranslation();

  const translationPrefix = `${translationJSONPrefix}.form.fields`;

  return (
    <Form>
      <InputsContainer>
        <AppSelectField
          name="group"
          label={t(`${translationPrefix}.group.label`)}
          placeholder={
            isGroupsLoading
              ? t(`${translationPrefix}.group.loading`)
              : t(`${translationPrefix}.group.placeholder`)
          }
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          menuData={groupsData.map((grp) => {
            return {
              label: `${grp?.name} (${grp?.policyCount} polic${
                grp?.policyCount > 1 ? 'ies' : 'y'
              })`,
              value: grp?.id,
            };
          })}
        />
      </InputsContainer>

      <InputsContainer>
        <AppAutocompleteField
          name="users"
          label={t(`${translationPrefix}.users.label`)}
          placeholder={
            isUsersLoading
              ? t(`${translationPrefix}.users.loading`)
              : t(`${translationPrefix}.users.placeholder`)
          }
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          options={usersData}
          getOptionLabel={(option) =>
            `${option?.firstName || '-'} ${option?.lastName || ''} (${
              option?.emailId
            })`
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={index}
                label={`${option?.firstName || '-'} ${
                  option?.lastName || ''
                } (${option?.emailId})`}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </InputsContainer>

      <FormButtons
        resetForm={resetForm}
        dirty={dirty}
        isSubmitting={isSubmitting || formSending}
      />
    </Form>
  );
};

const AccessControlAddUsersGroupPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [initialFormData, setInitialFormData] = useState(defaultDataState);
  const [formData, setFormData] = useState(defaultDataState);
  const [showAlert, setShowAlert] = useState({
    formUpdateError: false,
    formUpdateSuccess: false,
  });
  const [isEdit, setIsEdit] = useState(false);
  const [entityId, setEntityId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [
    createRBAC,
    {
      data: createdData,
      error: createError,
      isError: isCreateError,
      isLoading: isCreateLoading,
      isSuccess: isCreateSuccess,
    },
  ] = useCreateRBACMutation();

  const [
    updateRBAC,
    {
      data: updatedData,
      error: updateError,
      isError: isUpdateError,
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateRBACMutation();

  const [
    getRBAC,
    {
      data: rbacData,
      isError: isDataError,
      isLoading: isDataLoading,
      isSuccess: isDataSuccess,
    },
  ] = useLazyGetOneRBACQuery();

  const [
    getGroupsRBAC,
    {
      data: groupsData,
      isLoading: isGroupsLoading,
      isSuccess: isGroupsSuccess,
    },
  ] = useLazyGetAllRBACQuery();

  const [
    getUsersRBAC,
    { data: usersData, isLoading: isUsersLoading, isSuccess: isUsersSuccess },
  ] = useLazyGetAllRBACQuery();

  useEffect(() => {
    const tempEntityId = searchParams.get('id');

    if (tempEntityId) {
      setIsEdit(true);
      getRBAC({ endpoint: RBAC_USERS_GROUP_ENDPOINT, id: tempEntityId });
      setEntityId(tempEntityId);
    }

    getGroupsRBAC({ endpoint: RBAC_GROUPS_ENDPOINT });
    getUsersRBAC({ endpoint: RBAC_USERS_ENDPOINT });
  }, []);

  useEffect(() => {
    if (isDataSuccess && rbacData && usersData) {
      const tempFormData = {
        group: rbacData?.id,
        users: filterByIds(rbacData?.users, usersData),
      };

      setFormData(tempFormData);
      setInitialFormData(tempFormData);
    }
  }, [rbacData, usersData]);

  const closeAlert = () => {
    const tempAlert = { ...showAlert };
    Object.keys(tempAlert).forEach((a) => (tempAlert[a] = false));
    setShowAlert(tempAlert);
  };

  useEffect(() => {
    const tempAlert = { ...showAlert };
    if (isCreateError || isUpdateError) {
      tempAlert.formUpdateError = true;
      tempAlert.formUpdateSuccess = false;
      const tempErrorMessage =
        createError?.data?.message ||
        updateError?.data?.message ||
        t(`${translationJSONPrefix}.form.errorAlertLabel`);
      setErrorMessage(tempErrorMessage);
      setShowAlert(tempAlert);
    }
  }, [isCreateError, isUpdateError]);

  useEffect(() => {
    const tempAlert = { ...showAlert };
    if (isCreateSuccess || isUpdateSuccess) {
      tempAlert.formUpdateSuccess = true;
      tempAlert.formUpdateError = false;
      setShowAlert(tempAlert);
      getRBAC({
        endpoint: RBAC_USERS_GROUP_ENDPOINT,
        id: createdData?.id || updatedData?.id,
      });
    }
  }, [isCreateSuccess, isUpdateSuccess]);

  useEffect(() => {
    if (showAlert.formUpdateError || showAlert.formUpdateSuccess)
      setTimeout(closeAlert, 5000);
  }, [showAlert.formUpdateError, showAlert.formUpdateSuccess]);

  return (
    <PageContainer drawer={SECTIONS.ACCESS_CONTROL}>
      <AddPageHeader
        routes={[
          {
            title: t(`${translationJSONPrefix}.mainPage.title`),
            endpoint: mainPageRoute,
          },
        ]}
        isEdit={isEdit}
      />

      <FormContainer>
        <AlertStyled
          onClose={closeAlert}
          severity={showAlert.formUpdateError ? 'error' : 'success'}
          style={{
            marginTop:
              showAlert.formUpdateError || showAlert.formUpdateSuccess
                ? 10
                : -60,
          }}
        >
          {showAlert.formUpdateSuccess &&
            (isEdit
              ? t(`${translationJSONPrefix}.form.updatedAlertLabel`)
              : t(`${translationJSONPrefix}.form.createdAlertLabel`))}
          {showAlert.formUpdateError && errorMessage}
        </AlertStyled>

        {isDataLoading ? (
          <Loader label={t(`${translationJSONPrefix}.form.loadingLabel`)} />
        ) : (
          <AppForm
            initialValues={formData}
            validationSchema={formSchema(t)}
            onSubmit={(values, { setSubmitting }) => {
              const valuesToSend = isEdit
                ? getChangedValues(values, initialFormData)
                : { ...values };

              if (valuesToSend?.users)
                valuesToSend.users = valuesToSend.users.map((v) => v.id);

              if (isEdit)
                updateRBAC({
                  endpoint: RBAC_USERS_GROUP_ENDPOINT,
                  id: entityId,
                  update: valuesToSend,
                });
              else
                createRBAC({
                  endpoint: RBAC_USERS_GROUP_ENDPOINT,
                  body: valuesToSend,
                });

              setSubmitting(false);
            }}
            formRenderFunction={(formikState) =>
              formRenderFunction(
                formikState,
                isCreateLoading || isUpdateLoading,
                groupsData || [],
                isGroupsLoading,
                usersData || [],
                isUsersLoading,
              )
            }
          />
        )}
      </FormContainer>
    </PageContainer>
  );
};

export default AccessControlAddUsersGroupPage;
